import { ACTION_COURSE, ACTION_PAYMENT, ACTION_SUBSCRIPTION } from '@/redux/actions';
import { CONSTANTS, HELPERS, ipxUrl } from '@/utils';
import { Dialog, Transition } from '@headlessui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@/components/atoms';
import ClampedText from '@/components/atoms/ClampedText';
import { useAddToCart } from '@/hooks/cart/useCartMutations';
import { CourseTopicList } from '@/routes/Course/components/CourseTopicList';
import CourseTopicListItem from '@/routes/Course/components/CourseTopicListItem';
import PATH from '@/routes/path';
import { Icon } from '@iconify/react';
import parser from 'html-react-parser';
import ReactPixel from 'react-facebook-pixel';
import { useTranslation } from 'react-i18next';
import { BiInfoCircle } from 'react-icons/bi';
import { CgDanger } from 'react-icons/cg';
import { MdClose } from 'react-icons/md';
import { notify } from 'react-notify-toast';
import ReactPlayer from 'react-player';
import { useHistory } from 'react-router-dom';

const CourseDetailModal = ({ isOpen, onClose, courseSlug, courseUuid }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const addToCart = useAddToCart();

  // Redux state
  const payload = useSelector((state) => state.course.detail.payload);
  const isLoading = useSelector((state) => state.course.detail.loading);
  const packageState = useSelector((state) => state.package);
  const setting = useSelector((state) => state.client.payload?.available_order);
  const { data: packageData, loading: isPackageDataLoading } = packageState.list;

  // Local state
  const [activeResource, setActiveResource] = useState(null);
  const [activeChapterIndex, setActiveChapterIndex] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isChangeResource, setIsChangeResource] = useState(false);
  const [activeTab, setActiveTab] = useState('tentang');
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);

  const starterPrice = useMemo(() => {
    if (packageData && packageData.length > 0) {
      return Math.min(...packageData.map((item) => item.price));
    }
    return 0;
  }, [packageData]);

  const totalModule = payload?.modules.length || 0;
  const totalTopic = payload?.modules.reduce((total, chapter) => total + chapter.topics.length, 0);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && courseSlug) {
      dispatch(ACTION_SUBSCRIPTION.getSubcriptionPackage());
      dispatch(ACTION_COURSE.getCourseDetailData(courseSlug));
    }
  }, [isOpen, courseSlug, dispatch]);

  // Handle initial active resource
  const handleInitialActiveResource = useCallback(() => {
    if (payload) {
      const chapters = payload.modules;
      let selectedResource = null;

      chapters.some((chapter, chapterIndex) => {
        const isSelected = chapter.topics.some((resource, resourceIndex) => {
          const isLastIndexChapter = chapterIndex === chapters.length - 1;
          const isLastIndexResource = resourceIndex === chapter.topics.length - 1;
          selectedResource = {
            ...resource,
            isLastResource: isLastIndexChapter && isLastIndexResource,
            chapterIndex: chapterIndex,
          };
          return !resource.locked && !resource.completed;
        });

        if (isSelected) setActiveChapterIndex(chapterIndex);
        return isSelected;
      });

      setActiveResource(selectedResource);
    }
  }, [payload?.modules]);

  // Handle resource change
  const handleChangeActiveResource = (selectedResource) => {
    if (selectedResource.locked && !payload.enrolled) {
      notify.show(t('anda_belum_terdaftar_di_course_ini'), 'warning');
    } else if (selectedResource.locked) {
      notify.show(t('anda_harus_menyelesaikan_resource_sebelumnya_terlebih_dahulu'), 'warning');
    } else {
      payload.modules.forEach((chapter, index) => {
        if (chapter.topics.some((topic) => topic.uuid === selectedResource.uuid)) {
          setActiveChapterIndex(index);
          selectedResource.chapterIndex = index;
        }
      });
      setActiveResource(selectedResource);
      setIsChangeResource(true);
    }
  };

  // Handle buy course
  const handleBuyCourse = () => {
    const { title, price, uuid } = payload;
    dispatch(ACTION_PAYMENT.resetCheckoutState());
    sessionStorage.removeItem('CART_PAYMENT_DATA');

    const addToCartPayload = {
      currency: 'IDR',
      value: price,
      order_type: 'purchase_course',
      page_source: 'Course Detail Modal',
      items: [
        {
          item_id: uuid,
          item_name: title,
          item_category: selectedCategory?.name,
          item_category2: selectedCategory?.parent,
        },
      ],
    };

    sessionStorage.setItem('utm_data_add_to_cart', JSON.stringify(addToCartPayload));
    HELPERS.sendEventGA('add_to_cart', addToCartPayload);

    onClose();
    history.push(`${PATH.PAYMENT}`, {
      title,
      productType: CONSTANTS.PRODUCT_TYPE.COURSE,
      price,
      slug: courseSlug,
      uuid,
    });
  };

  // Handle add to cart
  const handleAddToCart = () => {
    if (!payload) {
      notify.show('Tidak dapat menambahkan ke keranjang, data kursus tidak lengkap', 'error');
      return;
    }

    addToCart.mutate(
      {
        id: payload.uuid,
        name: payload.title,
        price: payload.price,
        originalPrice: payload.unit_price,
        discount: payload.discount,
        quantity: 1,
        image: payload.thumbnail,
        itemType: CONSTANTS.PRODUCT_TYPE.COURSE.value,
        details: {
          instructor: payload.instructor,
          category: payload.categories && payload.categories[0] ? payload.categories[0].name : '',
          slug: payload.slug,
        },
      },
      {
        onSuccess: (result) => {
          if (result && result.added === false) {
            notify.show('Kursus sudah ada di keranjang', 'warning');
          } else {
            notify.show('Berhasil ditambahkan ke keranjang', 'success');

            const category = payload.categories && payload.categories[0] ? payload.categories[0] : null;
            const gaPayload = {
              currency: 'IDR',
              value: payload.price,
              order_type: 'purchase_course',
              page_source: 'Course Detail Modal',
              items: [
                {
                  item_id: payload.uuid,
                  item_name: payload.title,
                  item_category: category?.name,
                  item_category2: category?.parent,
                  price: payload.price,
                  quantity: 1,
                },
              ],
            };

            sessionStorage.setItem('utm_data_add_to_cart', JSON.stringify(gaPayload));
            HELPERS.sendEventGA('add_to_cart', gaPayload);

            ReactPixel.track('AddToCart', {
              content_ids: [payload.uuid],
              content_type: 'course',
              content_name: payload.title,
              currency: 'IDR',
              value: payload.price,
            });
          }
        },
        onError: () => {
          notify.show('Gagal menambahkan ke keranjang', 'error');
        },
      }
    );
  };

  // Handle enroll course
  const handleEnrollCourse = () => {
    if (payload) {
      dispatch(
        ACTION_PAYMENT.enrollCourse(payload?.uuid, (props) => {
          if (props?.payload?.success) {
            window.location.reload();
          }
        })
      );
    }
  };

  // Handle start learning (redirect to course page)
  const handleStartLearning = () => {
    onClose();
    history.push(`${PATH.COURSE}/${payload.slug}`);
  };

  // Effect for payload processing
  useEffect(() => {
    if (payload) {
      handleInitialActiveResource();

      const cat = payload?.categories && payload.categories[0] ? payload.categories[0] : null;
      setSelectedCategory(cat);

      // Track modal view
      ReactPixel.track('ViewContent', {
        content_ids: [payload.uuid],
        content_type: 'course',
        content_name: payload.title,
        currency: 'IDR',
        value: payload.price,
      });

      const gaData = {
        currency: 'IDR',
        value: payload.price,
        page_source: 'Course Detail Modal',
        items: [
          {
            item_id: payload.uuid,
            item_name: payload.title,
            price: payload.price,
            quantity: 1,
            item_category: cat?.name,
            item_category2: cat?.parent,
            item_type: 'Course',
          },
        ],
      };

      HELPERS.sendEventGA('view_item', gaData);
    }
  }, [payload?.uuid, handleInitialActiveResource]);

  useEffect(() => {
    if (isChangeResource) {
      setShowVideoPlayer(true);
    }
  }, [isChangeResource]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveResource(null);
      setActiveChapterIndex(null);
      setShowVideoPlayer(false);
      setSelectedCategory(null);
      setIsChangeResource(false);
      setActiveTab('tentang');
    }
  }, [isOpen]);

  if (!payload && isLoading) {
    return (
      <Transition appear show={isOpen} as={React.Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-50" />
            </Transition.Child>

            <span className="inline-block h-screen align-middle" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold">Detail Kursus</h2>
                  <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                    <MdClose size={24} />
                  </button>
                </div>
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p>Memuat detail kursus...</p>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    );
  }

  if (!payload) {
    return null;
  }

  // Handle thumbnail source and fallback
  const thumbnailSrcRaw = payload?.thumbnail || 'https://placehold.co/600x400?text=Course';
  const ipxOptions = { quality: 80, format: 'webp' };
  const thumbnailSrc = ipxUrl(thumbnailSrcRaw, { ...ipxOptions });

  const isUserCanBuy = setting?.includes(CONSTANTS.PURCHASE_OPTIONS.BUY_LEARNING_PROGRAM);
  const isUserCanSubscribe = setting?.includes(CONSTANTS.PURCHASE_OPTIONS.SUBSCRIBE_ALL);
  const isUserHasSubscription = payload?.has_subscription;
  const isUserEnrolled = payload?.enrolled;

  return (
    <>
      <Transition appear show={isOpen} as={React.Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-50" />
            </Transition.Child>

            <span className="inline-block h-screen align-middle" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl max-h-[90vh] overflow-y-auto cart-scrollbar">
                {/* Header with Background Image and Overlay */}
                <div className="lg:flex relative bg-[#7349BB] lg:bg-primary items-center justify-center overflow-hidden px-6 py-8 text-white">
                  {/* Background Image */}
                  <div
                    className="hidden lg:block absolute inset-0 bg-center bg-cover"
                    style={{
                      backgroundImage: `url('${thumbnailSrc}')`,
                      opacity: 0.2,
                    }}
                  />

                  {/* Overlay Warna */}
                  <div className="hidden lg:block absolute inset-0 bg-[linear-gradient(to_right,rgba(53,32,89,0.7),rgba(115,73,187,0.7))]" />

                  {/* Content */}
                  <div className="relative z-10 w-full space-y-6">
                    <button
                      onClick={onClose}
                      className="absolute top-[-16px] right-[-16px] p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                    >
                      <MdClose size={24} className="text-white" />
                    </button>

                    <h1 className="text-2xl font-bold pr-12">{payload?.title}</h1>

                    <div className={'text-base text-white font-medium line-clamp-2'}>
                      {parser(payload?.description || '')}
                    </div>

                    {/* Course Stats */}
                    <div className="flex items-center space-x-6 text-sm">
                      <div className="flex items-center">
                        <Icon icon="ic:outline-book" className="mr-2" width="20" height="20" />
                        <span>
                          {totalModule} bab • {totalTopic} jam
                        </span>
                      </div>
                    </div>

                    {/* Category Tags */}
                    <div className="flex flex-wrap gap-2">
                      {payload?.categories?.map((cat, index) => (
                        <div key={index} className="bg-white bg-opacity-20 rounded-full px-3 py-1 text-sm">
                          {cat.name}
                        </div>
                      ))}
                      {payload?.parent_category && (
                        <div className="bg-white bg-opacity-20 rounded-full px-3 py-1 text-sm">
                          {payload.parent_category.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Content Area */}
                <div className="px-6 py-6">
                  {/* Navigation Tabs */}
                  <div className="flex border-b border-gray-200 mb-6">
                    <button
                      onClick={() => setActiveTab('tentang')}
                      className={`px-4 py-2 font-medium text-sm border-b-2 ${
                        activeTab === 'tentang'
                          ? 'border-green-500 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Tentang
                    </button>
                    <button
                      onClick={() => setActiveTab('materi')}
                      className={`px-4 py-2 font-medium text-sm border-b-2 ${
                        activeTab === 'materi'
                          ? 'border-green-500 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Materi
                    </button>
                    <button
                      onClick={() => setActiveTab('instruktur')}
                      className={`px-4 py-2 font-medium text-sm border-b-2 ${
                        activeTab === 'instruktur'
                          ? 'border-green-500 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Instruktur
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="gap-6">
                    {/* Main Content */}
                    <div>
                      {activeTab === 'tentang' && (
                        <div>
                          {/* Subscription notice */}
                          {isUserHasSubscription && (
                            <div className="mb-6 p-4 rounded-xl flex items-start bg-indigo-50 text-indigo-700">
                              <div className="flex-shrink-0 mr-4 pt-1">
                                <BiInfoCircle className="text-primary-700 text-lg" />
                              </div>
                              <div className="flex-1">
                                <p>Paket berlangganan kamu aktif. Silakan klik Mulai Belajar!</p>
                              </div>
                            </div>
                          )}

                          {/* Yang Akan Anda Pelajari */}
                          <div className="mb-6">
                            <h3 className="font-bold text-xl mb-4">Tentang Kursus</h3>

                            {payload?.description && (
                              <ClampedText
                                content={payload.description}
                                initialClampLines={4}
                                classNames={{
                                  content: 'prose prose-md mt-4 lg:mt-6 description-content',
                                  button: 'text-green-500 text-sm font-medium hover:underline mt-1 mb-3',
                                }}
                              />
                            )}
                          </div>

                          {/* Untuk Siapa Kursus ini */}
                          <div className="mb-6">
                            <h3 className="text-xl font-bold mb-4">Untuk Siapa Kursus ini?</h3>
                            <div className="space-y-3">
                              {payload?.categories?.map((category, index) => (
                                <div key={index} className="flex items-start">
                                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                  <span>Yang tertarik dengan {category.name}</span>
                                </div>
                              ))}
                              {payload?.parent_category && (
                                <div className="flex items-start">
                                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                  <span>Pemula di bidang {payload.parent_category.name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {activeTab === 'materi' && (
                        <div>
                          <h3 className="text-xl font-bold mb-4">Materi Kursus</h3>
                          <div className="text-sm mb-4 text-gray-600 flex items-center space-x-4">
                            <div>{totalModule} bab</div>
                            <div>{totalTopic} materi</div>
                          </div>

                          {/* Video Player Section */}
                          {showVideoPlayer && activeResource?.resource_url && (
                            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                              <div className="flex justify-between items-center mb-4">
                                <h4 className="text-lg font-semibold">{activeResource?.title}</h4>
                                <button
                                  onClick={() => setShowVideoPlayer(false)}
                                  className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                                >
                                  <MdClose size={20} />
                                </button>
                              </div>
                              <div className="bg-white rounded-lg overflow-hidden">
                                {isLoading ? (
                                  <div className="flex items-center justify-center h-64">
                                    <div className="text-center">
                                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                                      <span>Memuat video...</span>
                                    </div>
                                  </div>
                                ) : activeResource.resource_url ? (
                                  <ReactPlayer
                                    controls
                                    width="100%"
                                    height="400px"
                                    playing
                                    url={activeResource.resource_url}
                                  />
                                ) : (
                                  <div className="flex items-center justify-center h-64 text-gray-500">
                                    Video tidak tersedia
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {payload.modules.length === 0 ? (
                            <div className="flex flex-col items-center justify-center p-6 space-y-5 text-center text-gray-400">
                              <CgDanger size={42} />
                              <span>{t('no_chapters_found')}</span>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {payload.modules.map((chapter, index) => (
                                <CourseTopicListItem
                                  key={chapter.uuid}
                                  title={chapter.title}
                                  resources={chapter.topics}
                                  activeResource={activeResource}
                                  onChangeActiveResource={(resource) => {
                                    if (resource.uuid === activeResource?.uuid) {
                                      setShowVideoPlayer(true);
                                    } else {
                                      handleChangeActiveResource(resource);
                                    }
                                  }}
                                  open={activeChapterIndex ? activeChapterIndex === index : index === 0}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {activeTab === 'instruktur' && (
                        <div>
                          <h3 className="text-xl font-bold mb-4">Instruktur Kursus</h3>
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center space-x-4">
                              <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200">
                                <img
                                  src={payload?.instructor_avatar || 'https://placehold.co/64x64?text=I'}
                                  alt={payload?.instructor}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div>
                                <div className="font-bold text-lg">{payload?.instructor}</div>
                                <div className="text-gray-600">{payload?.instructor_detail}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default CourseDetailModal;
